import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DemoPropertyService } from '../../../services/demoPropertyService';

const BannerContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const DemoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
`;

const DemoTitle = styled.div`
  .main-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 4px 0;
    
    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    opacity: 0.9;
    margin: 0;
  }
`;

const ActionButton = styled.button`
  background: white;
  color: #667eea;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  min-width: 140px;
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover:not(:disabled) {
      background: #c82333;
    }
  }
  
  &.success {
    background: #28a745;
    color: white;
    
    &:hover:not(:disabled) {
      background: #218838;
    }
  }
`;

const DemoContent = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }
`;

const DemoInfo = styled.div`
  .description {
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 12px;
    opacity: 0.95;
  }
  
  .features {
    display: flex;
    gap: 20px;
    font-size: 14px;
    
    @media (max-width: 768px) {
      justify-content: center;
      flex-wrap: wrap;
      gap: 16px;
    }
  }
  
  .feature {
    display: flex;
    align-items: center;
    gap: 6px;
    opacity: 0.9;
    
    .icon {
      font-size: 16px;
    }
  }
`;

const DemoActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const DemoStats = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  
  .stats-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
    text-align: center;
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 16px;
    text-align: center;
  }
  
  .stat {
    .value {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 4px;
      display: block;
    }
    
    .label {
      font-size: 12px;
      opacity: 0.8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: rgba(40, 167, 69, 0.15);
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  
  .message-title {
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  .booking-link {
    font-family: monospace;
    background: rgba(255, 255, 255, 0.2);
    padding: 8px 12px;
    border-radius: 4px;
    word-break: break-all;
    font-size: 13px;
    margin: 8px 0;
  }
  
  .copy-button {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.5);
    color: white;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    margin-top: 8px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const DemoPropertyBanner = ({ onRefreshData }) => {
  const [demoStatus, setDemoStatus] = useState({
    hasDemoData: false,
    demoProperties: 0,
    demoBookings: 0,
    demoProperty: null
  });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Check demo status on mount
  useEffect(() => {
    checkDemoStatus();
  }, []);

  const checkDemoStatus = async () => {
    try {
      setLoading(true);
      const status = await DemoPropertyService.getDemoDataStatus();
      setDemoStatus(status);
    } catch (error) {
      console.error('Error checking demo status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDemo = async () => {
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      
      const result = await DemoPropertyService.createDemoProperty();
      
      setSuccess({
        type: 'created',
        property: result.property,
        bookingUrl: result.bookingUrl,
        stats: {
          totalShowings: result.totalShowings,
          upcomingShowings: result.upcomingShowings,
          completedShowings: result.completedShowings
        }
      });
      
      // Refresh demo status
      await checkDemoStatus();
      
      // Refresh parent data
      if (onRefreshData) {
        onRefreshData();
      }
      
    } catch (error) {
      setError(`Failed to create demo property: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDemo = async () => {
    if (!window.confirm('Are you sure you want to delete all demo data? This will remove the demo property and all its bookings.')) {
      return;
    }
    
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);
      
      const result = await DemoPropertyService.cleanupDemoData();
      
      setSuccess({
        type: 'deleted',
        stats: {
          deletedProperties: result.deletedProperties,
          deletedBookings: result.deletedBookings
        }
      });
      
      // Refresh demo status
      await checkDemoStatus();
      
      // Refresh parent data
      if (onRefreshData) {
        onRefreshData();
      }
      
    } catch (error) {
      setError(`Failed to delete demo data: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyBookingLink = () => {
    if (success?.bookingUrl) {
      navigator.clipboard.writeText(success.bookingUrl);
      alert('Booking link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <BannerContainer>
        <DemoContent>
          <DemoInfo>
            <div className="description">
              <LoadingSpinner />
              Checking for demo data...
            </div>
          </DemoInfo>
        </DemoContent>
      </BannerContainer>
    );
  }

  return (
    <BannerContainer>
      <DemoHeader>
        <DemoTitle>
          <h3 className="main-title">
            {demoStatus.hasDemoData ? '🎬 Demo Property Active' : '🚀 Try ShowingBooker with Demo Data'}
          </h3>
          <p className="subtitle">
            {demoStatus.hasDemoData 
              ? 'Your demo property is ready to explore!' 
              : 'Create a sample property with bookings to explore all features'
            }
          </p>
        </DemoTitle>
        
        <DemoActions>
          {!demoStatus.hasDemoData ? (
            <ActionButton 
              onClick={handleCreateDemo} 
              disabled={creating}
              className="success"
            >
              {creating ? (
                <>
                  <LoadingSpinner />
                  Creating...
                </>
              ) : (
                '✨ Create Demo Property'
              )}
            </ActionButton>
          ) : (
            <ActionButton 
              onClick={handleDeleteDemo} 
              disabled={deleting}
              className="danger"
            >
              {deleting ? (
                <>
                  <LoadingSpinner />
                  Deleting...
                </>
              ) : (
                '🗑️ Remove Demo Data'
              )}
            </ActionButton>
          )}
        </DemoActions>
      </DemoHeader>

      <DemoContent>
        <DemoInfo>
          <div className="description">
            {demoStatus.hasDemoData 
              ? 'Your demo property includes sample bookings, tenant info, and a working booking link you can test.'
              : 'Get a complete property with upcoming & completed showings, tenant notifications, and a live booking link to test the full experience.'
            }
          </div>
          
          <div className="features">
            <div className="feature">
              <span className="icon">🏠</span>
              Sample Property
            </div>
            <div className="feature">
              <span className="icon">📅</span>
              {demoStatus.hasDemoData ? `${demoStatus.demoBookings} Bookings` : '5 Sample Bookings'}
            </div>
            <div className="feature">
              <span className="icon">👥</span>
              Tenant & Agent Info
            </div>
            <div className="feature">
              <span className="icon">🔗</span>
              Live Booking Link
            </div>
          </div>
        </DemoInfo>
      </DemoContent>

      {/* Demo Stats */}
      {demoStatus.hasDemoData && (
        <DemoStats>
          <div className="stats-title">Demo Data Overview</div>
          <div className="stats-grid">
            <div className="stat">
              <span className="value">{demoStatus.demoProperties}</span>
              <span className="label">Properties</span>
            </div>
            <div className="stat">
              <span className="value">{demoStatus.demoBookings}</span>
              <span className="label">Bookings</span>
            </div>
            <div className="stat">
              <span className="value">{demoStatus.demoProperty?.city || 'SF'}</span>
              <span className="label">Location</span>
            </div>
            <div className="stat">
              <span className="value">$3,200</span>
              <span className="label">Rent</span>
            </div>
          </div>
        </DemoStats>
      )}

      {/* Success Messages */}
      {success && (
        <SuccessMessage>
          {success.type === 'created' && (
            <>
              <div className="message-title">✅ Demo Property Created Successfully!</div>
              <div>
                Property: <strong>{success.property.address_line1}</strong><br/>
                Showings: {success.stats.upcomingShowings} upcoming, {success.stats.completedShowings} completed
              </div>
              <div className="booking-link">{success.bookingUrl}</div>
              <button className="copy-button" onClick={handleCopyBookingLink}>
                📋 Copy Booking Link
              </button>
            </>
          )}
          
          {success.type === 'deleted' && (
            <>
              <div className="message-title">🧹 Demo Data Removed Successfully!</div>
              <div>
                Deleted: {success.stats.deletedProperties} properties, {success.stats.deletedBookings} bookings
              </div>
            </>
          )}
        </SuccessMessage>
      )}

      {/* Error Messages */}
      {error && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.15)',
          border: '1px solid rgba(220, 53, 69, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '16px',
          color: '#fff'
        }}>
          <strong>❌ Error:</strong> {error}
        </div>
      )}
    </BannerContainer>
  );
};

export default DemoPropertyBanner;